import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { groupMissingByStem, summarizeGroups } from '../utils/missingLevelsGrouping';
import { classNamePattern } from '../utils/classNamePattern';
import RuleEditorDialog from './RuleEditorDialog';

// READ-ONLY triage aid for the Missing Levels view. It groups the classes ALREADY LOADED for the
// current view by a deterministic schedule-variant stem and orders them by how many of those loaded
// classes share each name, WITHOUT widening the grid. It never fetches, mutates, or changes
// matching/overrides. Clicking a pattern focuses the grid on it via the existing search box
// (onSelectPattern).
//
// WHAT THIS PANEL DOES AND DOES NOT CLAIM (measured 2026-08-11 over all 450 recurring TY2026
// candidates): the ordering is FREQUENCY, not rule value. Occurrence barely tracks a rule's real
// impact -- the top ten by displayed count and the top ten by actual warehouse reach overlap by
// 1 of 10, and 81.6% of patterns reach classes outside the tracking year on screen. Semantic quality
// cannot be judged from here either: the two cheap signals we tested were a heuristic (unreliable
// enough to misfile "Jack Rabbits") and structurally blind (99.8% of candidates match nothing that
// already has a level, because they come from unmapped families by construction). So the copy below
// states what was observed in this view and sends the steward to Preview for anything more. Do not
// reintroduce value/quality/priority language, and do not infer that a name seen once is a one-off.

const TOP_N = 10;

// The candidate handed to RuleEditorDialog. `stem` is the dialog's existing seed field, so the
// authored pattern becomes a CONTIGUOUS SUBSTRING of a real class name instead of the grouping key.
//
// WHY: the stem deletes day/time tokens from anywhere and glues the edges, so it is frequently not a
// substring of any member name and matches ZERO classes under production's LIKE. Measured
// 2026-08-11: that is true of 273 of the 450 recurring candidates this panel lists (60.7%) -- every
// one of them would open the dialog pre-loaded with a pattern that cannot be published. Same
// contract ClassLevelPanel adopted in S5b.
//
// NOTHING ELSE CHANGES: grouping, the ×N counts, the click-to-filter key (onSelectPattern still
// sends g.stem) and every evidence field the dialog reads (occurrences, variantCount, programType,
// sampleNames, representativeFranchises -> scope default) are untouched. When classNamePattern's
// level-signature guard fires it returns the representative's FULL name, and that conservative
// result is kept rather than broadened. The preview remains the authoritative reach check.
const seedFor = (g) => ({ ...g, stem: classNamePattern(g.representativeName || g.stem) || g.stem });

export default function MissingLevelsInsights({ rows, truncated, onSelectPattern, levels, trackingyear }) {
  const [open, setOpen] = useState(true);
  const [proposeGroup, setProposeGroup] = useState(null); // the candidate open in the authoring dialog

  const { groups, summary } = useMemo(() => {
    const g = groupMissingByStem(rows);
    return { groups: g, summary: summarizeGroups(g) };
  }, [rows]);

  if (!rows || rows.length === 0) return null;

  const recurring = groups.filter((g) => g.occurrences >= 2);
  const top = recurring.slice(0, TOP_N);
  const moreCount = recurring.length - top.length;

  const patternItems = top.map((g) => {
    const facilityHint = g.representativeFacilities[0]
      ? `e.g. ${g.representativeFacilities[0]}${g.facilityCount > 1 ? ` +${g.facilityCount - 1}` : ''}`
      : null;
    const clickable = typeof onSelectPattern === 'function';
    return (
      <Box
        key={g.stem}
        sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5,
          borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Chip size="small" label={`×${g.occurrences}`} color="warning" variant="outlined"
          sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 46 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {clickable ? (
            <Link component={ButtonBase} onClick={() => onSelectPattern(g.stem)}
              underline="hover" sx={{ textAlign: 'left', fontWeight: 600 }}
              title="Focus the grid on this pattern">
              {g.stem}
            </Link>
          ) : (
            <Typography component="span" sx={{ fontWeight: 600 }}>{g.stem}</Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {g.franchiseCount} franchise{g.franchiseCount === 1 ? '' : 's'}
            {g.variantCount > 1 ? ` · ${g.variantCount} name variants` : ''}
            {facilityHint ? ` · ${facilityHint}` : ''}
          </Typography>
        </Box>
        {g.programType && (
          <Chip size="small" variant="outlined" label={g.programType}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />
        )}
        <Button size="small" variant="text" onClick={() => setProposeGroup(seedFor(g))}
          sx={{ minWidth: 0, px: 0.75, fontSize: 12 }} title="Author a mapping rule from this pattern">
          Propose rule
        </Button>
      </Box>
    );
  });

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <ButtonBase onClick={() => setOpen((v) => !v)}
          aria-expanded={open} aria-controls="missing-levels-insights"
          sx={{ borderRadius: 1, px: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {open ? '▾' : '▸'} Recurring unmapped class names
          </Typography>
        </ButtonBase>
        <Typography variant="caption" color="text.secondary">
          {summary.recurringGroups} recurring pattern{summary.recurringGroups === 1 ? '' : 's'}
          {' · '}{summary.singletonGroups} name{summary.singletonGroups === 1 ? '' : 's'} seen once in this view
          {' · '}{summary.totalOfferings} class{summary.totalOfferings === 1 ? '' : 'es'} in view
        </Typography>
      </Box>

      <Collapse in={open}>
        <Box id="missing-levels-insights" sx={{ mt: 1 }}>
          {recurring.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              No class name appears more than once in this view. Broader filters or another tracking
              year may still show recurrence.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Patterns appearing more than once in this view (day and time collapsed), ordered by
                occurrence. Each ×N counts only the classes loaded here, not a rule’s reach. Preview
                a rule to measure its full warehouse impact across seasons.
              </Typography>
              {patternItems}
              {moreCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  +{moreCount} more recurring pattern{moreCount === 1 ? '' : 's'} — narrow the filters to see them.
                </Typography>
              )}
            </>
          )}
          {truncated && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              Results were truncated, so these counts cover only the loaded subset. Narrow the filters to load the rest.
            </Typography>
          )}
        </Box>
      </Collapse>

      <RuleEditorDialog
        open={Boolean(proposeGroup)}
        mode="create"
        group={proposeGroup}
        levels={levels}
        trackingyear={trackingyear}
        onClose={() => setProposeGroup(null)}
      />
    </Paper>
  );
}
